import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Group, JoinAnswer } from '../../types';
import { Avatar } from '../Avatar';
import { Button } from '../ui/Button';
import { Users, Lock, Globe, Settings } from 'lucide-react';
import { JoinQuestionsModal } from './JoinQuestionsModal';

interface GroupCardProps {
  group: Group;
  onJoin?: (groupId: number, answers?: JoinAnswer[]) => void;
  onLeave?: (groupId: number) => void;
  onSettings?: (groupId: number) => void;
  showActions?: boolean;
  isLoading?: boolean;
  className?: string;
}

export const GroupCard: React.FC<GroupCardProps> = ({
  group,
  onJoin,
  onLeave,
  onSettings,
  showActions = true,
  isLoading = false,
  className = '',
}) => {
  const [showJoinModal, setShowJoinModal] = useState(false);

  const isPrivate = group.privacy === 'private_';
  
  // Check membership status with multiple fallbacks
  const isMember = group.userMembershipStatus === 'joined' || 
                   group.member === true || 
                   (group.userRole && group.userRole !== null);
  
  const isPending = group.userMembershipStatus === 'pending' || 
                    group.hasPendingRequest === true;
  
  const canManage = group.userRole === 'owner' || 
                    group.userRole === 'moderator' || 
                    group.userRole === 'admin';


  const handleJoinClick = () => {
    if (isLoading) return;
    
    // If group is private or requires approval, show join questions modal
    // This will check if there are any questions to answer
    if (group.requiresApproval || group.privacy === 'private_') {
      setShowJoinModal(true);
    } else {
      // Direct join without questions (public groups)
      if (onJoin) {
        onJoin(group.id);
      }
    }
  };

  const handleJoinWithAnswers = async (answers: JoinAnswer[]) => {
    if (onJoin) {
      await onJoin(group.id, answers);
    }
  };

  const handleLeaveClick = () => {
    if (onLeave && !isLoading) {
      onLeave(group.id);
    }
  };

  const handleSettingsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onSettings && !isLoading) {
      onSettings(group.id);
    }
  };

  const renderActionButton = () => {
    if (!showActions) return null;

    // If user is already a member, show Leave button
    if (isMember) {
      return (
        <Button
          variant="outline"
          size="sm"
          onClick={handleLeaveClick}
          disabled={isLoading}
          className="w-full text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300 transition-all"
        >
          Rời nhóm
        </Button>
      );
    }

    // If user has a pending request, show Pending button
    if (isPending) {
      return (
        <Button
          variant="outline"
          size="sm"
          disabled
          className="w-full text-orange-600 border-orange-200 bg-orange-50 cursor-not-allowed"
        >
          Đang chờ phê duyệt
        </Button>
      );
    }

    // If user is not a member and no pending request, show Join button
    return (
      <Button
        variant="primary"
        size="sm"
        onClick={handleJoinClick}
        disabled={isLoading}
        className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 border-0 shadow-md hover:shadow-lg transition-all"
      >
        {group.requiresApproval ? 'Yêu cầu tham gia' : 'Tham gia ngay'}
      </Button>
    );
  };

  return (
    <div className={`group bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-2xl hover:border-blue-200 transition-all duration-300 transform hover:-translate-y-1 ${className}`}>
      {/* Cover Image with Overlay */}
      <div className="relative h-32 bg-gradient-to-br from-blue-500 via-cyan-500 to-teal-500 overflow-hidden">
        {group.coverUrl ? (
          <>
            <img
              src={group.coverUrl}
              alt={`${group.name} cover`}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
          </>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-blue-400/20 via-cyan-400/20 to-teal-400/20 backdrop-blur-3xl">
            <div className="absolute inset-0 flex items-center justify-center">
              <Users className="w-16 h-16 text-white/30" />
            </div>
          </div>
        )}        
        
        {/* Privacy Badge */}
        <div className="absolute top-3 right-3">
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full backdrop-blur-md ${
            isPrivate 
              ? 'bg-amber-500/90 text-white' 
              : 'bg-green-500/90 text-white'
          } shadow-lg`}>
            {isPrivate ? (
              <>
                <Lock className="w-3.5 h-3.5" />
                <span className="text-xs font-semibold">Riêng tư</span>
              </>
            ) : (
              <>
                <Globe className="w-3.5 h-3.5" />
                <span className="text-xs font-semibold">Công khai</span>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Group Info */}
      <div className="p-5">
        <div className="flex items-start space-x-4">
          {/* Group Avatar with Gradient Ring */}
          <div className="flex-shrink-0 -mt-10 relative">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-2xl blur-lg opacity-50 group-hover:opacity-75 transition-opacity"></div>
              <Avatar
                user={{
                  avatarUrl: group.avatarUrl,
                  displayName: group.name,
                  userId: group.id
                }}
                size="lg"
                className="relative ring-4 ring-white shadow-xl"
              />
            </div>
          </div>
          
          {/* Group Details */}
          <div className="flex-1 min-w-0 pt-2">
            <div className="flex items-start justify-between gap-2">
              <Link
                to={`/groups/${group.id}`}
                className="text-xl font-bold text-gray-900 hover:text-blue-600 transition-colors truncate group-hover:text-blue-600"
              >
                {group.name}
              </Link>
              
              {canManage && (
                <button
                  onClick={handleSettingsClick}
                  className="p-2 rounded-xl hover:bg-gradient-to-br hover:from-blue-50 hover:to-cyan-50 transition-all text-gray-400 hover:text-blue-600 flex-shrink-0"
                  title="Cài đặt nhóm"
                >
                  <Settings className="w-4 h-4" />
                </button>
              )}
            </div>
            
            {/* Description */}
            {group.description && (
              <p className="text-sm text-gray-600 mt-2 line-clamp-2 leading-relaxed">
                {group.description}
              </p>
            )}
            
            {/* Stats with Icons */}
            <div className="flex items-center flex-wrap gap-3 mt-3">
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg">
                <Users className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-semibold text-blue-700">{group.memberCount}</span>
                <span className="text-xs text-blue-600">thành viên</span>
              </div>
              
              {/* Membership Status Badge */}
              {isMember && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-green-50 to-emerald-100 text-green-700 rounded-lg shadow-sm">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  <span className="text-xs font-semibold">Đã tham gia</span>
                </span>
              )}
              
              {isPending && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-orange-50 to-amber-100 text-orange-700 rounded-lg shadow-sm">
                  <span className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></span>
                  <span className="text-xs font-semibold">Đang chờ</span>
                </span>
              )}
              
              {group.requiresApproval && !isMember && !isPending && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-600 rounded-lg text-xs">
                  <span>Cần phê duyệt</span>
                </span>
              )}
            </div>
            
            {/* Owner Info with Avatar */}
            {(group.ownerDisplayName || group.owner) && (
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span className="text-gray-400">Tạo bởi</span>
                  {group.owner ? (
                    <Link
                      to={`/profile/${group.owner.id}`}
                      className="flex items-center gap-1.5 font-medium text-blue-600 hover:text-blue-700 transition-colors"
                    >
                      {group.owner.avatarUrl && (
                        <img src={group.owner.avatarUrl} alt="" className="w-5 h-5 rounded-full ring-2 ring-blue-100" />
                      )}
                      <span>{group.owner.displayName}</span>
                  </Link>
                ) : (
                  <span className="font-medium text-gray-700">
                    {group.ownerDisplayName}
                  </span>
                )}
              </div>
            </div>
            )}
            
            {/* Action Button with Gradient */}
            <div className="mt-4">
              {renderActionButton()}
            </div>
          </div>
        </div>
      </div>

      {/* Join Questions Modal */}
      <JoinQuestionsModal
        isOpen={showJoinModal}
        onClose={() => setShowJoinModal(false)}
        groupId={group.id}
        groupName={group.name}
        onJoin={handleJoinWithAnswers}
        isLoading={isLoading}
      />
    </div>
  );
};